<template>
  <v-row>
    <v-dialog v-model="showDialog" scrollable max-width="850px">
      <v-skeleton-loader
        v-if="loading"
        type="article, actions"
      ></v-skeleton-loader>
      <v-card v-if="!loading">
        <v-card-title>
          <v-panel>
            <span style="font-size: 20px;">
              {{
                !data.id
                  ? $t("wkt.admin.training-course.new-window.title")
                  : $t("wkt.admin.training-course.edit-window.title")
              }}
            </span>
          </v-panel>
        </v-card-title>
        <v-card-text>
          <v-container @wheel="onWheel">
            <v-form ref="form">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="data.reference"
                    :label="$t('wkt.admin.training-course.edit-window.reference')"
                    autocomplete="off"
                    maxlength="100"
                    counter="100"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="8">
                  <v-text-field
                    v-model="data.name"
                    :label="$t('wkt.admin.training-course.edit-window.name')"
                    autocomplete="off"
                    :rules="[validationRules.required]"
                    maxlength="150"
                    counter="150"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="7">
                  <v-autocomplete
                    v-model="data.trainingCategoryId"
                    :items="trainingCategories"
                    item-text="name"
                    item-value="id"
                    :label="$t('wkt.admin.training-course.edit-window.training-category')"
                    :rules="[validationRules.required]"
                    :disabled="isReadOnly"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="data.estimatedDurationHours"
                    :label="$t('wkt.admin.training-course.edit-window.estimated-duration-hours')"
                    autocomplete="off"
                    type="number"
                    step="0.25"
                    min="0"
                    :disabled="isReadOnly"
                  />
                </v-col>
                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-checkbox
                    v-model="data.isActive"
                    :label="$t('wkt.admin.training-course.edit-window.is-active')"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="data.description"
                    :label="$t('wkt.admin.training-course.edit-window.description')"
                    auto-grow
                    rows="3"
                    maxlength="1000"
                    counter="1000"
                    :disabled="isReadOnly"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <div style="font-size: 12px; color: #757575; margin-bottom: 8px;">
                    {{ $t("wkt.admin.training-course.edit-window.objectives-program") }}
                  </div>
                  <TiptapVuetify
                    :vuetify-instance="$vuetify"
                    v-model="data.objectivesProgram"
                    :placeholder="courseObjectivesProgramPlaceholder"
                    :extensions="editorExtensions"
                    :editable="!isReadOnly"
                    :card-props="{ flat: true }"
                    @wheel="onWheel"
                    style="border: 1px solid rgba(0,0,0,.12); border-radius: 6px; min-height: 240px;"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="5">
                  <div style="font-size: 12px; color: #757575; margin-bottom: 8px;">
                    {{ $t("wkt.admin.training-course.edit-window.thumbnail") }}
                  </div>
                  <v-img
                    v-if="currentThumbnailUrl"
                    :src="currentThumbnailUrl"
                    max-height="180"
                    contain
                    style="border: 1px solid #E0E0E0; border-radius: 8px; background: #FAFAFA;"
                  />
                  <div
                    v-else
                    style="height: 180px; border: 1px dashed #D0D0D0; border-radius: 8px; background: #FAFAFA; display: flex; align-items: center; justify-content: center; color: #757575; text-align: center; padding: 16px;"
                  >
                    {{ $t("wkt.admin.training-course.edit-window.thumbnail-empty") }}
                  </div>
                </v-col>
                <v-col cols="12" md="7" class="d-flex flex-column justify-center">
                  <div style="font-size: 13px; color: #757575; margin-bottom: 16px;">
                    {{
                      data.id
                        ? $t("wkt.admin.training-course.edit-window.thumbnail-help")
                        : $t("wkt.admin.training-course.edit-window.thumbnail-save-first")
                    }}
                  </div>
                  <div class="d-flex flex-wrap">
                    <v-btn
                      color="#185EA9"
                      text
                      class="mr-2 mb-2"
                      @click="uploadThumbnail()"
                      :disabled="isReadOnly || !data.id || loadingThumbnail"
                      style="font-size: 0.9em; font-weight: 400;"
                    >
                      <v-icon left color="#185EA9">insert_photo</v-icon>
                      {{
                        $t(
                          data.thumbnailPath
                            ? "wkt.admin.training-course.edit-window.thumbnail-replace"
                            : "wkt.admin.training-course.edit-window.thumbnail-upload",
                        ).toUpperCase()
                      }}
                    </v-btn>
                    <v-btn
                      v-if="data.thumbnailPath"
                      color="#185EA9"
                      text
                      class="mb-2"
                      @click="deleteThumbnail()"
                      :disabled="isReadOnly || loadingThumbnail"
                      style="font-size: 0.9em; font-weight: 400;"
                    >
                      <v-icon left color="#185EA9">delete</v-icon>
                      {{ $t("wkt.admin.training-course.edit-window.thumbnail-delete").toUpperCase() }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            v-if="data.id"
            color="#ff0000"
            @click="confirmDelete()"
            :disabled="isReadOnly"
          >
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">{{
              $t("wkt.admin.common.dialog-buttons.delete").toUpperCase()
            }}</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#185EA9"
            text
            @click="onSave()"
            style="font-size: 0.9em; font-weight: 400;"
            :disabled="isReadOnly"
          >
            {{ $t("wkt.admin.common.dialog-buttons.ok").toUpperCase() }}
          </v-btn>
          <v-btn
            text
            @click="showDialog = false"
            style="font-size: 0.9em; font-weight: 400;"
          >
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref="dlgConfirm"
      v-bind:title="$t('wkt.admin.training-course.edit-window.confirm-dialog.title')"
      v-bind:message="$t('wkt.admin.training-course.edit-window.confirm-dialog.text')"
      v-bind:titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      v-bind:titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      v-on:confirmed="deleteItem()"
    />
  </v-row>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog";
import api from "../../../api/worktime.js";
import gwt from "../../../api/gwt.js";
import { Constants } from "../../common/Constants.js";
import {
  TiptapVuetify,
  Bold,
  Italic,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  History,
  Paragraph,
  HardBreak,
} from "tiptap-vuetify";

export default {
  name: "TrainingCourseDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog,
    TiptapVuetify,
  },
  data() {
    return {
      showDialog: false,
      loading: true,
      loadingThumbnail: false,
      data: {},
      profile: null,
      trainingCategories: [],
      editorExtensions: [
        History,
        Link,
        Underline,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        Bold,
        Paragraph,
        HardBreak,
      ],
    };
  },
  computed: {
    sessionModuleTraining() {
      return this.profile?.moduleTraining;
    },
    userHasAnyAccess() {
      return this.sessionModuleTraining !== Constants.DENY_ALL;
    },
    isReadOnly() {
      return this.sessionModuleTraining === Constants.ALLOW_READ_ONLY;
    },
    currentThumbnailUrl() {
      if (!this.data || !this.data.thumbnailPath) {
        return null;
      }
      return this.getPubilcAssetUrl(this.data.thumbnailPath);
    },
    courseObjectivesProgramPlaceholder() {
      return this.$t("wkt.admin.training-course.edit-window.objectives-program-placeholder");
    },
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return;
      }

      event.stopPropagation();
    },
    // eslint-disable-next-line no-unused-vars
    open(action, payload) {
      this.profile = gwt.getSessionProfile();

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      this.showDialog = true;

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }

      this.loading = true;
      this.loadTrainingCategories(() => {
        if (!payload) {
          this.data = {
            companyId: this.profile.companyId,
            isActive: true,
            thumbnailPath: null,
            objectivesProgram: "",
          };
          this.loading = false;
          return;
        }

        api.getTrainingCourseById(
          payload,
          (res) => {
            this.data = {
              ...res,
              objectivesProgram: res && res.objectivesProgram ? res.objectivesProgram : "",
            };
            this.loading = false;
          },
          (error) => {
            this.$emit("show-alert", {
              text: error,
              color: "error",
              timeout: 3000,
            });
            this.loading = false;
          },
        );
      });
    },
    loadTrainingCategories(callback) {
      api.getTrainingCategories(
        (res) => {
          this.trainingCategories = res || [];
          callback();
        },
        (error) => {
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
          this.loading = false;
        },
      );
    },
    onSave() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.loading = true;
      this.data.estimatedDurationHours =
        this.data.estimatedDurationHours === "" || this.data.estimatedDurationHours === null
          ? null
          : Number(this.data.estimatedDurationHours);

      api.storeTrainingCourse(
        this.data,
        (res) => {
          this.loading = false;
          if (res.rsOK) {
            this.showDialog = false;
            this.$emit("refresh");
          } else {
            this.$emit("show-alert", {
              text: res.message,
              color: "error",
              timeout: 3000,
            });
          }
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    uploadThumbnail() {
      if (!this.data || !this.data.id || this.isReadOnly) {
        return;
      }

      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.onchange = (event) => {
        const file = event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;
        if (!file) {
          return;
        }

        const formData = new FormData();
        formData.append("file", file, file.name);

        this.loadingThumbnail = true;
        api.uploadTrainingCourseThumbnail(
          this.data.id,
          formData,
          (res) => {
            this.loadingThumbnail = false;
            this.data.thumbnailPath = res;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-course.edit-window.thumbnail-upload-success"),
              color: "success",
              timeout: 3000,
            });
          },
          (error) => {
            this.loadingThumbnail = false;
            this.$emit("show-alert", {
              text: error && error.message ? error.message : error,
              color: "error",
              timeout: 3000,
            });
          },
        );
      };

      input.click();
    },
    deleteThumbnail() {
      if (!this.data || !this.data.id || !this.data.thumbnailPath || this.isReadOnly) {
        return;
      }

      this.loadingThumbnail = true;
      api.deleteTrainingCourseThumbnail(
        this.data.id,
        (res) => {
          this.loadingThumbnail = false;
          if (res.rsOK) {
            this.data.thumbnailPath = null;
            this.$emit("show-alert", {
              text: this.$t("wkt.admin.training-course.edit-window.thumbnail-delete-success"),
              color: "success",
              timeout: 3000,
            });
            return;
          }

          this.$emit("show-alert", {
            text: res.message,
            color: "error",
            timeout: 3000,
          });
        },
        (error) => {
          this.loadingThumbnail = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.getTrainingCourseUsageCount(
        this.data.id,
        (res) => {
          if (res > 0) {
            this.loading = false;
            this.$emit("show-alert", {
              text: this.$t(
                "wkt.admin.training-course.edit-window.associated_items_error",
                [res],
              ),
              color: "error",
              timeout: 3000,
            });
            return;
          }

          api.deleteTrainingCourse(
            this.data.id,
            (deleteRes) => {
              this.loading = false;
              this.showDialog = false;

              if (!deleteRes.rsOK) {
                this.$emit("show-alert", {
                  text: deleteRes.message,
                  color: "error",
                  timeout: 3000,
                });
                return;
              }

              this.$emit("refresh");
            },
            (error) => {
              this.loading = false;
              this.$emit("show-alert", {
                text: error && error.message ? error.message : error,
                color: "error",
                timeout: 3000,
              });
            },
          );
        },
        (error) => {
          this.loading = false;
          this.$emit("show-alert", {
            text: error && error.message ? error.message : error,
            color: "error",
            timeout: 3000,
          });
        },
      );
    },
  },
};
</script>
